import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { constructSupportChatHref } from "../../../common/infrastructure/routes/routePaths";
import { getErrorToast, getSuccessToast } from "../../../common/infrastructure/utilities/toastUtility";
import { supportChatCategoryLabels, supportChatPriorityLabels } from "../../support/models/supportChatModels";
import { IAiCreateSupportTicketRequest, IAiSupportDraft, aiAssistantTexts } from "../models/aiAssistantModels";
import {
  IAiSupportDraftFormModel,
  aiSupportDraftFormSchema,
  getAiSupportDraftFormDefaultValues,
} from "../models/aiSupportDraftFormModel";
import { readSupportChatId } from "../utilities/aiAssistantDataUtility";

interface IUseAiSupportDraftCardArgs {
  /** The stored answer that holds the draft; null while the answer is still streaming. */
  messageId: number | null;
  draft: IAiSupportDraft;
  /** Set once the draft was sent: the ticket it became. */
  supportChatId: string | null;
}

/**
 * The ticket the assistant prepared: the user may edit its text and sends it with one button,
 * which creates a normal support ticket (with the conversation's images) and opens it.
 */
export const useAiSupportDraftCard = ({ messageId, draft, supportChatId }: IUseAiSupportDraftCardArgs) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sentChatId, setSentChatId] = useState<string | null>(supportChatId);

  const form = useForm<IAiSupportDraftFormModel>({
    resolver: yupResolver(aiSupportDraftFormSchema),
    defaultValues: getAiSupportDraftFormDefaultValues(draft.content),
  });

  useEffect(() => {
    if (supportChatId) setSentChatId(supportChatId);
  }, [supportChatId]);

  const { mutate, isLoading: isSending } = useApiMutation<unknown>({
    onSuccess: (data) => {
      const chatId = readSupportChatId(data);
      if (!chatId) {
        getErrorToast(aiAssistantTexts.draftSendFailed);
        return;
      }
      setSentChatId(chatId);
      getSuccessToast("Запитването е изпратено към екипа на Planny.");
      queryClient.invalidateQueries({ queryKey: [endpointsMetadata.aiAssistant.getConversation] });
      queryClient.invalidateQueries({ queryKey: [endpointsMetadata.supportChat.getAll] });
      navigate(constructSupportChatHref(chatId));
    },
  });

  const submit = form.handleSubmit((values) => {
    if (!messageId || sentChatId) return;
    const payload: IAiCreateSupportTicketRequest = {
      messageId,
      title: draft.title,
      category: draft.category,
      priority: draft.priority,
      content: values.content.trim(),
    };
    mutate({
      endpoint: endpointsMetadata.aiAssistant.createSupportTicket,
      method: "post",
      payload,
      disableSuccessToast: true,
      errorMessage: aiAssistantTexts.draftSendFailed,
    });
  });

  return {
    form,
    submit,
    isSending,
    canSend: !!messageId && !sentChatId,
    sentChatHref: sentChatId ? constructSupportChatHref(sentChatId) : null,
    categoryLabel: supportChatCategoryLabels[draft.category] ?? "",
    priorityLabel: supportChatPriorityLabels[draft.priority] ?? "",
  };
};
